import React, { useCallback, useState } from "react";
import { Alert } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as Instagram2Icon } from "./assets/instagram-2.svg";

import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ReactComponent as ShuffleIcon } from "./assets/shuffle.svg";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { ReactComponent as MessageIcon } from "./assets/message.svg";
import { ReactComponent as MetaIcon } from "./assets/meta.svg";
import { ReactComponent as StarIcon } from "./assets/star.svg";
import { useInstagramAccount } from "../../../../providers/InstagramAccountProvider/useInstagramAccount";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

export const InstagramAccountCard: React.FC<{
  hideReconnectOption?: boolean;
  isClickable?: boolean;
  variant?: "card" | "tile";
}> = ({ hideReconnectOption, isClickable, variant = "card" }) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const { instagramAccount, isConnected, loginInstagramAccount } =
    useInstagramAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();
  const { navigateToPage } = useNavigation();
  const { entityId } = useEntitySelectedProvider();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [disconnectInstagramAccount, { isLoading }] =
    integrationsApi.useDisconnectInstagramAccountMutation();

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const accountName =
    instagramAccount?.username || instagramAccount?.business_id || "";

  const handleRemoveAccount = useCallback(async () => {
    const resp = await disconnectInstagramAccount({
      entityId,
      meta: {
        successMessage: t({
          id: "client-support-page.instagram-was-disconnected",
          defaultMessage: "Instagram was disconnected",
        }),
      },
    });

    if (resp && "data" in resp) {
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
    }
  }, [
    disconnectInstagramAccount,
    entityId,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
  ]);

  const disconnectAlert = (
    <Alert
      isOpen={isDisconnectAlertOpen}
      onRequestClose={() => setIsDisconnectAlertOpen(false)}
      title={t({
        id: "client-support-page.disconnect-account",
        defaultMessage: "Disconnect Account",
      })}
      content={t(
        {
          id: "client-support-page.disconnect-instagram-account",
          defaultMessage:
            "Are you sure you want to disconnect {account} Instagram account?",
        },
        {
          account: (
            <span className="font-medium text-primary">{accountName}</span>
          ),
        }
      )}
      buttons={[
        {
          text: t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          }),
          color: "grey",
          role: "close",
        },
        {
          text: t({
            id: "client-support-page.disconnect",
            defaultMessage: "Disconnect",
          }),
          onClick: async (_, { closeAlert }) => {
            closeAlert();
            await handleRemoveAccount();
          },
        },
      ]}
    />
  );

  if (variant === "tile") {
    const hasAccount = !!instagramAccount;

    const handleClick = () => {
      if (!isClickable) {
        return;
      }

      if (hasAccount) {
        transitionTo({ name: "instagramAccount" });
        return;
      }

      loginInstagramAccount();
    };

    return (
      <CRMAccountTile
        isClickable={isClickable}
        isConnected={hasAccount}
        title={t({
          id: "client-support-page.instagram",
          defaultMessage: "Instagram",
        })}
        onClick={handleClick}
        icon={<Instagram2Icon />}
        features={
          hasAccount
            ? [
                {
                  icon: <EmailIcon />,
                  label: instagramAccount?.username ?? "",
                },
                {
                  icon: <EditIcon />,
                  label: instagramAccount?.name ?? "",
                },
              ]
            : [
                {
                  icon: <MetaIcon />,
                  label: t({
                    id: "client-support-page.instagram-tile-feature-meta-partner",
                    defaultMessage: "Meta Business Partner",
                  }),
                },
                {
                  icon: <MessageIcon />,
                  label: t({
                    id: "client-support-page.instagram-tile-feature-dm-replies",
                    defaultMessage: "Handle DM replies",
                  }),
                },
                {
                  icon: <StarIcon />,
                  label: t({
                    id: "client-support-page.instagram-tile-feature-ai-replies",
                    defaultMessage: "AI powered replies",
                  }),
                },
                {
                  icon: <ShuffleIcon />,
                  label: t({
                    id: "client-support-page.instagram-tile-feature-automated-workflows",
                    defaultMessage: "Automated workflows",
                  }),
                },
              ]
        }
        status={
          hasAccount ? (isConnected ? "connected" : "disconnected") : undefined
        }
      />
    );
  }

  if (!instagramAccount) return null;

  return (
    <>
      <CRMAccountCard
        img={instagramAccount.profile_picture_url}
        title={instagramAccount.username || instagramAccount.business_id}
        buttonProps={
          !hideReconnectOption && !isConnected
            ? {
                text: t({
                  id: "client-support-page.reconnect",
                  defaultMessage: "Reconnect",
                }),
                onClick: loginInstagramAccount,
              }
            : undefined
        }
        onClick={
          isClickable
            ? () => transitionTo({ name: "instagramAccount" })
            : undefined
        }
        icon={InstagramIcon}
        description={
          isConnected
            ? `${instagramAccount.name}  •  ID: ${instagramAccount.business_id}`
            : t({
                id: "client-support-page.instagram-account-disconnected",
                defaultMessage:
                  "Instagram Account is disconnected. Please reconnect to continue using Instagram CRM",
              })
        }
        status={isConnected ? "connected" : "disconnected"}
        menuItems={[
          [
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: () => setIsDisconnectAlertOpen(true),
              isDisabled: isLoading,
            },
          ],
        ]}
      />
      {disconnectAlert}
    </>
  );
};
